import { useEffect, useState } from "react";
import { ChartLine, HandWithdraw, ShieldCheck, Target } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { inr, riskBadge } from "../../lib/format";
import { INVESTOR_TYPES, RANGE_DISCLAIMER } from "../../lib/plans";
import AppLayout, { PageLoader } from "../../components/AppLayout";
import { Badge } from "../../components/ui/badge";

const RISK_DETAILS = {
  Conservative: {
    summary: "Capital preservation first. Predominantly fixed income and liquid allocations with limited equity exposure.",
    horizon: "3–5 years", drawdown: "Low tolerance", equity: "0–25%",
  },
  Growth: {
    summary: "Balanced long-term growth. Meaningful equity allocation with stabilising fixed income and alternatives.",
    horizon: "5–7 years", drawdown: "Moderate tolerance", equity: "40–70%",
  },
  Prestige: {
    summary: "Maximum long-horizon appreciation. High conviction equity, private funds and alternatives with full drawdown acceptance.",
    horizon: "7–10+ years", drawdown: "High tolerance", equity: "70–100%",
  },
};

const Field = ({ label, value, testId }) => (
  <div className="glass-card p-5">
    <p className="label-caps">{label}</p>
    <p className="text-sm font-medium text-white mt-2" data-testid={testId}>{value || "—"}</p>
  </div>
);

export default function InvestmentRiskPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/investor/profile").then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <AppLayout title="Investment Risk"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!data) return <AppLayout title="Investment Risk"><PageLoader /></AppLayout>;

  const { profile } = data;
  const cfg = INVESTOR_TYPES[profile.investor_type] || null;
  const detail = RISK_DETAILS[profile.risk_profile] || RISK_DETAILS.Growth;

  return (
    <AppLayout title="Investment Risk" subtitle="Your investment mandate and risk profile, governing how your portfolio is managed.">
      <div className="fade-up space-y-8 max-w-4xl">

        <div className="space-y-5" data-testid="plan-section">
          <p className="label-caps text-emerald-400/80">Investment Plan</p>
          <div className="glass-card p-8 flex items-start gap-5">
            <div className="h-12 w-12 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 flex items-center justify-center shrink-0">
              <Target size={24} weight="duotone" />
            </div>
            <div>
              <p className="label-caps mb-1.5">Active Mandate</p>
              <h2 className="font-display text-3xl text-white" data-testid="plan-name">{profile.investment_plan || "—"}</h2>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed" data-testid="plan-objective">
                {profile.investment_objective}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Investor Type" value={profile.investor_type} testId="plan-investor-type" />
            <Field label="Series" value={profile.plan_series ? `${profile.plan_series} Series` : null} testId="plan-series" />
            <Field label="Tenure" value={profile.tenure_category || profile.investment_tenure} testId="plan-tenure" />
            <Field label="Withdrawal Option" value={profile.withdrawal_preference} testId="plan-withdrawal" />
          </div>

          {cfg && (
            <div className="glass-card p-6 flex items-start gap-5" data-testid="plan-range-card">
              <div className="h-11 w-11 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 flex items-center justify-center shrink-0">
                <HandWithdraw size={22} weight="duotone" />
              </div>
              <div>
                <p className="label-caps mb-1.5">Indicative Return Range</p>
                <p className="tnum font-display text-2xl text-emerald-300" data-testid="plan-range">{cfg.range}</p>
                <p className="text-[11px] text-zinc-500 mt-1.5" data-testid="plan-range-disclaimer">{RANGE_DISCLAIMER}</p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-5" data-testid="risk-section">
          <p className="label-caps text-emerald-400/80">Risk Profile</p>
          <div className="glass-card p-8 flex items-start gap-5">
            <div className="h-12 w-12 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 flex items-center justify-center shrink-0">
              <ChartLine size={24} weight="duotone" />
            </div>
            <div>
              <p className="label-caps mb-2">Assessed Risk Category</p>
              <div className="flex items-center gap-3">
                <h2 className="font-display text-3xl text-white" data-testid="risk-profile-name">{profile.risk_profile}</h2>
                <Badge variant="outline" className={`${riskBadge(profile.risk_profile)} text-[11px]`}>Active</Badge>
              </div>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed max-w-2xl" data-testid="risk-summary">{detail.summary}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="glass-card p-5">
              <p className="label-caps">Investment Horizon</p>
              <p className="tnum text-lg text-white mt-2" data-testid="risk-horizon">{detail.horizon}</p>
            </div>
            <div className="glass-card p-5">
              <p className="label-caps">Drawdown Tolerance</p>
              <p className="tnum text-lg text-white mt-2" data-testid="risk-drawdown">{detail.drawdown}</p>
            </div>
            <div className="glass-card p-5">
              <p className="label-caps">Indicative Equity Band</p>
              <p className="tnum text-lg text-white mt-2" data-testid="risk-equity-band">{detail.equity}</p>
            </div>
          </div>
        </div>

        <div className="glass-card p-6 flex items-start gap-4">
          <ShieldCheck size={20} className="text-emerald-500/70 shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-500 leading-relaxed">
            Your investment plan and risk profile are maintained by KMC Solutions and reviewed periodically with your
            Wealth Relationship Manager. To modify your mandate or reassess your risk profile, please contact your advisor.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
